import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { usePoll } from "@/lib/usePoll";
import { HardHat, RefreshCw, Loader2, Wrench } from "lucide-react";
import { SeasonalDecor, applySeasonalTheme } from "@/components/SeasonalDecor";

const MaintenanceContext = createContext({ active: false, message: "", theme: "none", loaded: false, refresh: () => {} });

export function MaintenanceProvider({ children }) {
  const [state, setState] = useState({ active: false, message: "", theme: "none", loaded: false });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/maintenance", { __flSkipReadCache: true, __flOnlineOnly: true });
      setState({ active: !!data.active, message: data.message || "", theme: data.theme || "none", loaded: true });
    } catch {
      // A failure to read status must never lock people out — fail open.
      setState((s) => ({ ...s, loaded: true }));
    }
  }, []);

  // Every API call already reports maintenance instantly (503 → event), so the poll only needs to be slow.
  usePoll(refresh, state.active ? 15000 : 60000, { hiddenMs: state.active ? 60000 : null, immediate: false });

  useEffect(() => {
    refresh();
    const onEvt = (e) => {
      if (e?.detail) {
        setState((s) => ({ active: !!e.detail.active, message: e.detail.message || s.message, theme: s.theme, loaded: true }));
      }
      // Re-verify shortly after, in case it has since been switched back off.
      setTimeout(refresh, 2000);
    };
    window.addEventListener("fieldlink:maintenance", onEvt);
    const onTheme = () => refresh();
    window.addEventListener("fieldlink:theme", onTheme);
    return () => { window.removeEventListener("fieldlink:maintenance", onEvt); window.removeEventListener("fieldlink:theme", onTheme); };
  }, [refresh]);

  useEffect(() => { applySeasonalTheme(state.theme); }, [state.theme]);

  return (
    <MaintenanceContext.Provider value={{ ...state, refresh }}>
      {children}
      <SeasonalDecor theme={state.theme} />
    </MaintenanceContext.Provider>
  );
}

export const useMaintenance = () => useContext(MaintenanceContext);

export function MaintenanceScreen({ message }) {
  const { refresh } = useMaintenance();
  const [checking, setChecking] = useState(false);

  const checkAgain = async () => {
    setChecking(true);
    await refresh();
    setTimeout(() => setChecking(false), 700);
  };

  return (
    <main data-testid="maintenance-screen"
      className="min-h-screen w-full flex items-center justify-center bg-zinc-950 text-zinc-100 px-5 py-12 relative overflow-hidden">
      <div aria-hidden="true"
        className="absolute inset-0 opacity-[0.07]"
        style={{ backgroundImage: "repeating-linear-gradient(45deg, #FF5F15 0 18px, transparent 18px 36px)" }} />
      <div className="relative w-full max-w-lg">
        <div className="flex items-center gap-3 mb-8">
          <img src="/brand/fieldlink-mark.png" alt="" aria-hidden="true" className="h-9 w-auto" />
          <span className="font-display font-black tracking-tight text-lg">
            FIELDLINK<span className="text-[#FF5F15]">NZ</span>
          </span>
        </div>

        <div className="border border-zinc-800 bg-zinc-900/80 backdrop-blur p-8 sm:p-10">
          <div className="inline-flex items-center justify-center h-16 w-16 bg-[#FF5F15] mb-6">
            <HardHat className="h-8 w-8 text-zinc-950" strokeWidth={2.2} />
          </div>

          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF5F15] mb-3">
            <Wrench className="h-3.5 w-3.5" /> Scheduled maintenance
          </div>

          <h1 data-testid="maintenance-title"
            className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight mb-4">
            We'll be right back
          </h1>

          <p data-testid="maintenance-message" className="text-zinc-300 text-base leading-relaxed">
            {message || "Sorry, we're currently doing some maintenance to the app and will be back up and running shortly. Thanks for your patience."}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button type="button" onClick={checkAgain} disabled={checking} data-testid="maintenance-check-again"
              className="inline-flex items-center gap-2 bg-[#FF5F15] hover:bg-[#e5530f] disabled:opacity-60 text-zinc-950 font-bold px-5 py-2.5 transition-colors">
              {checking
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking…</>
                : <><RefreshCw className="h-4 w-4" /> Check again</>}
            </button>
            <span className="text-xs text-zinc-500">This page updates automatically when we're back.</span>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-zinc-600">
          Need urgent help? Email <a href="mailto:support@fieldlink.co.nz" className="text-zinc-400 hover:text-zinc-200 underline">support@fieldlink.co.nz</a>
        </p>
      </div>
    </main>
  );
}
